import { useMemo, useState } from "react";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { WorkoutAchievementType } from "../../../../../common/enums";
import { IWorkoutAchievementsViewModel } from "../models/workoutAchievementsViewModel";

export interface IUseWorkoutAchievementsArgs {
  /**
   * Achievements endpoint to hit. Defaults to the caller-scoped
   * `GetMyWorkoutAchievements`; the admin user-profile view passes
   * `GetUserWorkoutAchievements` together with a `userId` in `extraParams`.
   */
  endpoint?: string;
  /** Query-key base (kept separate so distinct data sources don't share a cache). */
  queryKeyBase?: string;
  /** Extra query-string params (e.g. `{ userId }`). */
  extraParams?: Record<string, string | number>;
}

/** `null` = every badge type. */
export type AchievementFilter = WorkoutAchievementType | null;

/**
 * Owns the achievements tab's query and the badge-type filter. The list is derived
 * server-side from completed sets, so it's refetched whenever a set toggle invalidates
 * the query key (see useWorkoutPlanDialog).
 */
export const useWorkoutAchievements = ({
  endpoint = endpointsMetadata.workoutPlan.getMyWorkoutAchievements,
  queryKeyBase = endpointsMetadata.workoutPlan.getMyWorkoutAchievements,
  extraParams,
}: IUseWorkoutAchievementsArgs = {}) => {
  const [filter, setFilter] = useState<AchievementFilter>(null);

  const query = new URLSearchParams();
  Object.entries(extraParams ?? {}).forEach(([key, value]) =>
    query.set(key, String(value)),
  );
  const queryString = query.toString();
  const requestUrl = queryString ? `${endpoint}?${queryString}` : endpoint;

  const { data, isLoading } = useApiQuery<IWorkoutAchievementsViewModel>({
    queryKey: [queryKeyBase, extraParams ?? {}],
    endpoint: requestUrl,
    globalIsLoading: false,
  });

  const achievements = useMemo(() => {
    const all = data?.achievements ?? [];
    return filter === null ? all : all.filter((a) => a.type === filter);
  }, [data, filter]);

  return {
    summary: data?.summary,
    achievements,
    hasAny: (data?.summary.totalCount ?? 0) > 0,
    isLoading,
    filter,
    setFilter,
  };
};
